import Cookies from 'js-cookie';
import { useIntl } from 'react-intl';

import {
  APIlogin,
  APIrefreshToken,
  APIlogout,
  APIgetAccountSwitchToken,
  APIauthorizeIntegration,
  APIdeauthorizeIntegration,
  cookieParams,
  getRefreshToken,
  removeAuthCookie,
  getAuthTokenName,
} from '../api/Auth';
import { APIintegrationAuthenticateExternalUser } from '../api/Integrations';
import {
  APIfetchUserProfile,
  APIfetchUserProfileById,
  APIupdateUserSettings,
  APIconnectUser,
} from '../api/User';

import {
  setSettings,
  setFileSizes,
  setUserFields,
  setLanguageMessages,
  setFeatures,
  setCoupons,
  setSecondSidebarOpen,
} from './App';

import {
  getUrlParam,
  setRollbarPerson,
} from '../helpers';
import { sendAdobePostMessage } from '../helpers/Adobe';
import { addFlashMessage } from '../helpers/FlashMessages';
import { handleSortParams } from '../helpers/Sort';
import { sendPostMessage } from '../helpers/Mini';

export const SET_LOADING_STATUS = 'SET_LOADING_STATUS';
export const GET_USER_PROFILE = 'GET_USER_PROFILE';
export const SET_USER_SETTINGS = 'SET_USER_SETTINGS';
export const SET_USER_SETTING = 'SET_USER_SETTING';
export const CLEAR_USER_PROFILE = 'CLEAR_USER_PROFILE';
export const CONNECT_USER = 'CONNECT_USER';
export const SET_ORIGINAL_PROFILE = 'SET_ORIGINAL_PROFILE';

export const setLoadingStatus = status => ({
  type: SET_LOADING_STATUS,
  loading: status,
});

export const getUserProfile = profile => ({
  type: GET_USER_PROFILE,
  profile,
});

export const setUserSettings = settings => ({
  type: SET_USER_SETTINGS,
  settings,
});

export const setUserSetting = setting => ({
  type: SET_USER_SETTING,
  setting,
});

export const clearUserProfile = () => ({
  type: CLEAR_USER_PROFILE,
});

export const connectUserAction = () => ({
  type: CONNECT_USER,
});

export const setOriginalProfile = profile => ({
  type: SET_ORIGINAL_PROFILE,
  profile,
});

export const clearAuthData = () => {
  if (window.sp.mini) {
    const params = {
      ...cookieParams,
      domain: process.env.MIX_APP_DOMAIN,
      expires: 365,
      secure: true,
      sameSite: 'none',
    };
    Cookies.remove(getAuthTokenName(), params);
    localStorage.removeItem(getAuthTokenName());
    return;
  }

  removeAuthCookie('sp_access_token');
  removeAuthCookie('sp_expiration');
  removeAuthCookie('sp_refresh_token');
  removeAuthCookie('sp_switched_user');
  const { pathname } = window.location;
  if (pathname.indexOf('/login') < 0 && pathname.indexOf('/shares/') < 0) {
    window.location = '/login';
  }
};

export const switchToUser = (userUuid) => {
  if (userUuid) {
    Cookies.set('sp_switched_user', userUuid, cookieParams);
  } else {
    Cookies.remove('sp_switched_user', cookieParams);
  }
  window.location = '/files';
};

export const switchToAccount = (accountUuid, newWindow = false) => {
  APIgetAccountSwitchToken(accountUuid).then((response) => {
    if (response.url) {
      if (newWindow) {
        window.open(response.url, '_blank').focus();
      } else {
        window.location = response.url;
      }
    } else {
      const intl = useIntl();
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({
          defaultMessage: 'Something went wrong',
        }),
      });
    }
  }).catch(() => {
    const intl = useIntl();
    addFlashMessage({
      type: 'danger',
      text: intl.formatMessage({
        defaultMessage: 'Something went wrong',
      }),
    });
  });
};

export const logout = () => dispatch => (
  APIlogout()
    .then(() => {
      dispatch(clearUserProfile());
      clearAuthData();
    })
    .catch(() => {
      dispatch(clearUserProfile());
      clearAuthData();
    })
);

export const fetchUserProfile = () => dispatch => {
  dispatch(setLoadingStatus(true));
  return (
    APIfetchUserProfile()
      .then((response) => {
        if (!response.errors) {
          if (response.data.language_messages) {
            dispatch(setLanguageMessages(response.data.language_messages));
            delete response.data.language_messages;
          }
          if (response.data.client) {
            dispatch(setSettings(response.data.client.settings || {}));
            delete response.data.client.settings;
            dispatch(setFileSizes(response.data.client.file_sizes || []));
            delete response.data.client.file_sizes;
            dispatch(setUserFields(response.data.client.user_fields || []));
            delete response.data.client.user_fields;
            dispatch(setFeatures(response.data.client.all_features || []));
            delete response.data.client.all_features;
            if (response.data.client.coupons) {
              dispatch(setCoupons(response.data.client.coupons));
              delete response.data.client.coupons;
            }
            if (response.data.settings?.sidebar_open !== undefined) {
              dispatch(setSecondSidebarOpen(response.data.settings.sidebar_open));
            }
          }
          if (response.data.original_profile) {
            dispatch(setOriginalProfile(response.data.original_profile));
            delete response.data.original_profile;
            response.data.has_original_profile = true;
          }
          dispatch(getUserProfile(response.data));
          setRollbarPerson(response.data);

          handleSortParams();
        } else {
          dispatch(logout());
        }
        return response;
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          dispatch(logout());
        }
        dispatch(clearUserProfile());
      })
      .finally(() => {
        setLoadingStatus(false);
      })
  );
};

export const fetchUserProfileById = userId => dispatch => (
  APIfetchUserProfileById(userId)
    .then((response) => {
      if (!response.errors) {
        dispatch(getUserProfile(response));
      }
    })
    .catch(() => {
      dispatch(clearUserProfile());
    })
);

export const updateUserSettings = (userId, settings) => dispatch => (
  APIupdateUserSettings(userId, settings)
    .then((response) => {
      if (response && response.data) {
        dispatch(setUserSettings(response.data));
      }
    })
    .catch(() => {
      dispatch(clearUserProfile());
    })
);

const setSwitchedClientCookies = (response) => {
  if (response.original_client_uuid) {
    Cookies.set('sp_original_client_uuid', response.original_client_uuid, cookieParams);
  } else {
    Cookies.remove('sp_original_client_uuid', cookieParams);
  }
  if (response.original_user_uuid) {
    Cookies.set('sp_original_user_uuid', response.original_user_uuid, cookieParams);
  } else {
    Cookies.remove('sp_original_user_uuid', cookieParams);
  }
  if (response.switched_client_uuid) {
    Cookies.set('sp_switched_client_uuid', response.switched_client_uuid, cookieParams);
  } else {
    Cookies.remove('sp_switched_client_uuid', cookieParams);
  }
};

export const login = ({
  username,
  password,
  loginToken,
  jwtToken,
  googleCredential,
  g2faCode,
  codeChallenge,
  redirectUri,
  clientId,
  state,
  clearAuthDataOnError = true,
}) => dispatch => (
  APIlogin({
    username,
    password,
    loginToken,
    jwtToken,
    googleCredential,
    g2faCode,
    codeChallenge,
    redirectUri,
    clientId,
    state,
    fsbc: getUrlParam('fsbc'),
  })
    .then((response) => {
      if (!response.errors) {
        setSwitchedClientCookies(response.data);
        dispatch(fetchUserProfile());
      }
      return response;
    })
    .catch((error) => {
      if (clearAuthDataOnError) {
        dispatch(clearUserProfile());
        clearAuthData();
      }
      return error.response;
    })
);

export const refreshToken = () => (dispatch) => {
  if (getRefreshToken()) {
    dispatch(setLoadingStatus(true));
    return APIrefreshToken()
      .then((response) => {
        dispatch(setLoadingStatus(false));
        return response;
      })
      .catch((error) => {
        dispatch(clearUserProfile());
        clearAuthData();
        return error;
      });
  }

  dispatch(clearUserProfile());
  clearAuthData();

  return new Promise((resolve, reject) => { reject(); });
};

export const refreshTokenIfNecessary = () => (dispatch, getState) => (
  new Promise((resolve) => {
    const state = getState();
    if (!state.Auth.loading) {
      dispatch(refreshToken()).then(() => {
        resolve(true);
      }).catch(() => resolve(false));
    }
  })
);

export const refreshTokenInterceptor = () => {
  if (getRefreshToken()) {
    return APIrefreshToken()
      .then(response => (response.data.access_token))
      .catch((error) => {
        clearAuthData();
        return error;
      });
  }

  return new Promise((resolve, reject) => { reject(); });
};

export const connectUser = accessTokenUuid => dispatch => (
  APIconnectUser(accessTokenUuid)
    .then((response) => {
      dispatch(connectUserAction());
      return response.data;
    })
);

export const integrationAuth = (serviceId, code, state) => () => (
  APIauthorizeIntegration(serviceId, code, state)
    .then(response => response)
);

export const integrationDisconnect = serviceId => () => (
  APIdeauthorizeIntegration(serviceId)
    .then(response => response)
);

export const authenticateExternalUser = (serviceId, token) => () => (
  APIintegrationAuthenticateExternalUser(serviceId, token)
    .then(response => response.data)
);

export const logoutIntegration = () => {
  if (window.sp.miniConfig?.integrationId?.startsWith('adobe')) {
    sendAdobePostMessage('logout');
    if (process.env.MIX_NGROK_DEV_ENABLED) {
      localStorage.removeItem('adobe-auth-token');
    } else {
      sendAdobePostMessage('clear-storage', { key: 'adobe-auth-token' });
    }
  }
  if (window.sp.miniConfig?.integrationId === 'shopify') {
    sendPostMessage('logout');
  }
};
